import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { finalize, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { AddEthnicityDialogComponent } from '../../../shared/components/dialog/add-ethnicity/add-ethnicity.component';

interface Ethnicity {
  id: string;
  ethnicity: string;
  colour: string;
  ethnicities_id: string;
  ethnicity_color: string;
  ethnicity_name: string;
}

interface APIResponse {
  status: boolean;
  data: any;
  message?: string;
}

@Component({
  selector: 'app-ethnicity',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, TableComponent],
  templateUrl: './ethnicity.component.html',
  styleUrls: ['./ethnicity.component.scss'],
})
export class EthnicityComponent implements OnInit {
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild(TableComponent) tableComponent!: TableComponent;

  columns: string[] = ['ethnicity', 'colour'];
  ethnicityData: any[] = [];

  loading: boolean = false;
  isLoading: boolean = false;
  private dialogRef: any;

  constructor(
    public router: Router,
    private dialog: MatDialog,
    private httpService: HttpService,
    private http: HttpClient,
    private location: Location,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadEthnicities();
  }

  goBack(): void {
    this.location.back();
  }

  private loadEthnicities(): void {
    this.loading = true;
    this.httpService
      .get('settings/ethnicities')
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response: any) => {
          if (response?.status && response?.data) {
            this.ethnicityData = this.transformApiData(response.data);
          }
        },
        error: (err) => {
          console.error('Failed to load ethnicities:', err);
          this.showToast(err?.message || 'Failed to load ethnicities', 'error');
        },
      });
  }

  private transformApiData(apiData: Ethnicity[]) {
    return apiData.map((item) => ({
      id: item.ethnicities_id,
      ethnicity: item.ethnicity_name,
      colour: item.ethnicity_color,
    }));
  }

  openAddEthnicity(ethnicityData?: Ethnicity): void {
    const dialogData = ethnicityData
      ? {
          id: ethnicityData.id,
          ethnicityName: ethnicityData.ethnicity,
          color: ethnicityData.colour,
        }
      : null;

    this.dialogRef = this.dialog.open(AddEthnicityDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    this.dialogRef
      .afterClosed()
      .subscribe((result: { success: any; data: any }) => {
        if (result?.success) {
          this.loadEthnicities();
        }
      });
  }

  onDelete(ethnicity: Ethnicity): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm Delete',
      message: `Are you sure you want to delete ${ethnicity.ethnicity}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.deleteEthnicity(ethnicity);
        }
      });
  }

  private deleteEthnicity(ethnicity: Ethnicity): void {
    this.loading = true;
    this.httpService
      .delete<APIResponse>(`settings/ethnicities/${ethnicity.id}`)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          if (response?.status) {
            this.ethnicityData = this.ethnicityData.filter(
              (e) => e.id !== ethnicity.id,
            );
            this.adjustPaginationAfterDelete();
            this.showToast(
              response.message || 'Ethnicity deleted successfully!',
              'success',
            );
          } else {
            this.showToast(
              response?.message || 'Failed to delete ethnicity',
              'error',
            );
          }
        },
        error: (err) => {
          console.error('Delete error:', err);
          this.showToast(err?.message || 'Failed to delete ethnicity', 'error');
        },
      });
  }

  onEdit(ethnicity: Ethnicity): void {
    this.openAddEthnicity(ethnicity);
  }

  private adjustPaginationAfterDelete(): void {
    const totalPages = Math.ceil(
      this.ethnicityData.length / (this.tableComponent?.pageSize || 10),
    );
    if (
      this.tableComponent &&
      this.tableComponent.currentPage > totalPages &&
      totalPages > 0
    ) {
      this.tableComponent.currentPage = totalPages;
    }
  }

  private getErrorMessage(err: any): string {
    if (err.error?.message) return err.error.message;
    if (err.message) return err.message;
    if (err.status === 415)
      return 'Unsupported media type. Please check file format.';
    if (err.status === 413) return 'File too large. Maximum size is 5MB.';
    return 'An unexpected error occurred. Please try again.';
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    if (!message) return;

    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: [`${type}-toast`],
    });
  }
}
